<script lang="ts">
  import ChevronsUpDownIcon from '@lucide/svelte/icons/chevrons-up-down'
  import ChevronsDownUpIcon from '@lucide/svelte/icons/chevrons-down-up'
  import * as Collapsible from '$lib/components/ui/collapsible/index.js'
  import { buttonVariants } from '$lib/components/ui/button/index.js'

  import dayjs from 'dayjs'
  import AlIcon from '$lib/assets/icons/providers/ALIcon.svelte'
  import MalIcon from '$lib/assets/icons/providers/MALIcon.svelte'
  import MangaUpdates from '$lib/assets/icons/providers/MangaUpdates.svelte'
  import KitsuIcon from '$lib/assets/icons/providers/KitsuIcon.svelte'
  import NautiljonIcon from '$lib/assets/icons/providers/NautiljonIcon.svelte'
  import NovelUpdatesIcon from '$lib/assets/icons/providers/NovelUpdatesIcon.svelte'
  import RanobeDBIcon from '$lib/assets/icons/providers/RanobeDBIcon.svelte'
  import BookwalkerIcon from '$lib/assets/icons/providers/BookwalkerIcon.svelte'
  import WikidataIcon from '$lib/assets/icons/providers/WikidataIcon.svelte'
  import WebNovelIcon from '$lib/assets/icons/providers/WebNovelIcon.svelte'
  import HScrollWithThumb from '$lib/components/nuread/HScrollWithThumb.svelte'
  import { cn } from '$lib/utils'

  let {
    series,
    class: sclass,
    orientation = 'vertical',
  }: {
    series: any
    class?: string
    orientation?: 'vertical' | 'horizontal'
  } = $props()

  let open = $state(false)
  let infos = $state([])

  /** Keys stored for monaid API use only — not shown as external links. */
  const hiddenMediaIdKeys = new Set(['novelUpdateId'])

  function extSites(name: string, id: string) {
    if (name === 'webNovel' && typeof id === 'string' && /^https?:\/\//.test(id)) {
      return id
    }
    const sites: Record<string, string> = {
      anilist: `https://anilist.co/manga/${id}`,
      mal: `https://myanimelist.net/manga/${id}`,
      kitsu: `https://kitsu.app/manga/${id}`,
      mangaUpdate: `https://www.mangaupdates.com/series/${id}`,
      nautiljon: `https://www.nautiljon.com/${id}.html`,
      novelUpdate: `https://www.novelupdates.com/series/${id}/`,
      ranobedb: `https://ranobedb.org/series/${id}`,
      bookwalker: `https://bookwalker.jp/series/${id}`,
      wikidata: `https://www.wikidata.org/wiki/Q${id}`,
    }
    return sites[name]
  }

  const providerEntries = $derived(
    Object.entries(series?.MediaIds ?? {})
      .filter(
        ([key, value]) =>
          value != null &&
          value !== '' &&
          value !== 0 &&
          !hiddenMediaIdKeys.has(key)
      )
      .flatMap(([name, id]) => {
        const href = extSites(name, String(id))
        return href ? [{ name, id: String(id), href }] : []
      })
  )

  $effect(() => {
    infos = [
      { name: 'Format', value: series.kind },
      { name: 'Status', value: series.Status?.status ?? 'UNKNOWN' },
      {
        name: 'Start Date',
        value: series.Dates?.startDate?.year
          ? `${dayjs()
              .set('month', series.Dates?.startDate?.month)
              .format('MMM')
              .replace(
                'Invalid Date',
                ''
              )} ${series.Dates?.startDate?.day ?? ''}${series.Dates?.startDate?.day ? ',' : ''} ${series.Dates?.startDate?.year}`
          : undefined,
      },
      {
        name: 'End Date',
        value: series.Dates?.endDate?.year
          ? `${dayjs()
              .set('month', series.Dates?.endDate?.month)
              .format('MMM')
              .replace(
                'Invalid Date',
                ''
              )} ${series.Dates?.endDate?.day ?? ''} ${series.Dates?.endDate?.day ? ',' : ''} ${series.Dates?.endDate?.year}`
          : undefined,
      },
      ...(series.Staff ?? [])
        .filter((s) => s.name && s.role && s.role.indexOf('Lettering') == -1
        && s.role.indexOf('Translator') == -1  && s.role.indexOf('Editing') == -1
        && s.role.indexOf('Editor') == -1)
        .map((s) => ({ name: s.role, value: s.name })),
      {
        name: 'Synonyms',
        values: series.altTitles?.map(({ Title }) => Title) ?? [],
      },
      {
        name: 'Source',
        value: series.Origin?.source.replace('_', ' ') ?? 'ORIGINAL',
      },
    ]
  })

  const visibleInfos = $derived(
    infos.filter((i) => i.values?.length > 0 || i.value)
  )
</script>

{#snippet providerIcon(name: string)}
  {#if name == 'anilist'}
    <AlIcon class="h-7 w-7" />
  {:else if name == 'mal'}
    <MalIcon class="h-7 w-7" />
  {:else if name == 'mangaUpdate'}
    <MangaUpdates class="h-7 w-7" />
  {:else if name == 'kitsu'}
    <KitsuIcon color="orange" class="h-7 w-7" />
  {:else if name == 'nautiljon'}
    <NautiljonIcon class="h-7 w-7" />
  {:else if name == 'novelUpdate'}
    <NovelUpdatesIcon class="h-7 w-7" />
  {:else if name == 'ranobedb'}
    <RanobeDBIcon class="h-7 w-7" />
  {:else if name == 'bookwalker'}
    <BookwalkerIcon class="h-8 w-8" />
  {:else if name == 'wikidata'}
    <WikidataIcon class="h-7 w-7" />
  {:else if name == 'webNovel'}
    <WebNovelIcon class="h-7 w-7" />
  {/if}
{/snippet}

{#if orientation === 'horizontal'}
  <div
    class={cn(
      'media-facts rounded-lg border border-latte-surface1 dark:border-macchiato-surface1 bg-latte-mantle dark:bg-macchiato-mantle text-sm leading-5 font-bold dark:text-gray-300',
      sclass
    )}
  >
    <HScrollWithThumb scrollClass="flex items-stretch">
      <div
        class="media-ratings flex shrink-0 items-center justify-center border-r border-transparent px-4 py-5"
      >
        <a
          href=""
          class="media-rating flex items-center"
          target="_blank"
          rel="noreferrer"
        >
          <AlIcon />
          <span class="ml-1 font-bold"
            >{series.Score?.meanScore
              ? series.Score?.averageScore + '%'
              : 'No Score'}
          </span>
        </a>
      </div>
      {#each visibleInfos as { name, value, values }}
        <div
          class="media-fact flex shrink-0 flex-col justify-center gap-0.5 border-r border-transparent px-4 py-5 min-w-28 max-w-48"
        >
          <span class="text-xs opacity-70">{name}</span>
          <span
            class="media-fact-value text-sm leading-5 font-normal dark:text-gray-400 truncate"
          >
            {#if values}
              {values.slice(0, 2).join(', ')}{values.length > 2
                ? ` +${values.length - 2}`
                : ''}
            {:else}
              {value}
            {/if}
          </span>
        </div>
      {/each}
      <div class="media-fact flex shrink-0 items-center justify-center px-4 py-5">
        <div
          class="flex max-w-[16.5rem] flex-wrap justify-center gap-x-4 gap-y-2"
        >
          {#each providerEntries as { name, href }}
            <a
              href={href}
              class="inline-flex h-8 w-8 items-center justify-center opacity-80 transition duration-300 hover:scale-125 hover:opacity-100 dark:opacity-50 dark:hover:opacity-100"
              target="_blank"
              rel="noreferrer"
            >
              {@render providerIcon(name)}
            </a>
          {/each}
        </div>
      </div>
    </HScrollWithThumb>
  </div>
{:else}
  <div
    class={cn(
      'media-facts rounded-lg border border-latte-surface1 dark:border-macchiato-surface1 bg-latte-mantle dark:bg-macchiato-mantle text-sm leading-5 font-bold dark:text-gray-300',
      sclass
    )}
  >
    <div
      class="media-ratings flex border-b border-latte-surface1 dark:border-macchiato-surface1 px-4 py-2 font-medium items-center justify-center"
    >
      <a
        href=""
        class="media-rating flex items-center"
        target="_blank"
        rel="noreferrer"
      >
        <AlIcon />
        <span class="ml-1 font-bold"
          >{series.Score?.meanScore ? series.Score?.meanScore + '%' : 'No Score'}
        </span>
      </a>
    </div>
    {#each infos as { name, value, values }}
      {#if values?.length > 0 || value}
        <div
          class="media-fact flex justify-between border-b px-4 py-2 border-latte-surface1 dark:border-macchiato-surface1"
        >
          <span>{name}</span>
          <span
            class="media-fact-value ml-2 text-right text-sm leading-5 font-normal dark:text-gray-400"
          >
            {#if values}
              {#if values.length > 10 && name != 'Synonyms'}
                <Collapsible.Root bind:open class="space-y-2">
                  <div class="flex items-center">
                    <span class="grow" />
                    <h4 class="text-sm font-semibold">
                      {!open ? `+ ${values.length - 10} more...` : ''}
                    </h4>
                    <Collapsible.Trigger
                      class={buttonVariants({
                        variant: 'ghost',
                        size: 'sm',
                        class: 'w-1 p-0',
                      })}
                    >
                      {#if open}
                        <ChevronsDownUpIcon />
                      {:else}
                        <ChevronsUpDownIcon />
                      {/if}
                      <span class="sr-only">Toggle</span>
                    </Collapsible.Trigger>
                  </div>
                  {#each values.slice(0, 10) as v}
                    <span class="block">{v}</span>
                  {/each}
                  <Collapsible.Content class="space-y-2">
                    {#each values.slice(10) as v}
                      <span class="block">{v}</span>
                    {/each}
                  </Collapsible.Content>
                </Collapsible.Root>
              {:else}
                {#each values as v}
                  <span class="block">{v}</span>
                {/each}
              {/if}
            {:else}
              {value}
            {/if}
          </span>
        </div>
      {/if}
    {/each}
    <div
      class="media-fact flex justify-center border-b px-4 py-2 dark:border-gray-700"
    >
      <div
        class="flex max-w-[17.5rem] flex-wrap justify-center gap-x-5 gap-y-2"
      >
        {#each providerEntries as { name, href }}
          <a
            href={href}
            class="inline-flex h-10 w-10 items-center justify-center opacity-80 transition duration-300 hover:scale-125 hover:opacity-100 dark:opacity-50 dark:hover:opacity-100"
            target="_blank"
            rel="noreferrer"
          >
            {@render providerIcon(name)}
          </a>
        {/each}
      </div>
    </div>
  </div>
{/if}
